import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { GarageProvider } from "@/context/GarageContext";
import { Landing, NotFound, RequireAuth, AccountHub } from "@/pages/Public";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import { ResetPassword, VerifyEmail } from "@/pages/auth/TokenPages";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import AdminShell from "@/components/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminGarages from "@/pages/admin/AdminGarages";
import AdminGarageDetail from "@/pages/admin/AdminGarageDetail";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminSupport from "@/pages/admin/AdminSupport";
import { AdminAudit, AdminOutbox } from "@/pages/admin/AdminAudit";
import ProShell from "@/components/pro/ProShell";
import ProDashboard from "@/pages/pro/ProDashboard";
import Onboarding from "@/pages/pro/Onboarding";
import Customers from "@/pages/pro/Customers";
import CustomerDetail from "@/pages/pro/CustomerDetail";
import Vehicles from "@/pages/pro/Vehicles";
import Settings, { Employees } from "@/pages/pro/Settings";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalLanding from "@/pages/portal/PortalLanding";
import { PortalLogin, PortalSignup, PortalForgot } from "@/pages/portal/PortalAuth";
import PortalAccount from "@/pages/portal/PortalAccount";
import Planning from "@/modules/scheduling/Planning";
import Availability from "@/modules/scheduling/Availability";
import PortalBook from "@/modules/scheduling/PortalBook";
import Jobs from "@/modules/interventions/Jobs";
import JobDetail from "@/modules/interventions/JobDetail";
import PortalJob from "@/modules/interventions/PortalJob";
import Quotes from "@/modules/quotes/Quotes";
import QuoteEditor from "@/modules/quotes/QuoteEditor";
import PortalQuote from "@/modules/quotes/PortalQuote";
import Invoices from "@/modules/invoices/Invoices";
import InvoiceDetail from "@/modules/invoices/InvoiceDetail";
import PortalInvoice from "@/modules/invoices/PortalInvoices";
import Inbox from "@/modules/messaging/Inbox";
import FollowUps from "@/modules/followups/FollowUps";
import Referrals from "@/modules/referral/Referrals";
import Pricing from "@/pages/public/Pricing";
import Signup from "@/pages/public/Signup";
import SignupStatus from "@/pages/public/SignupStatus";
import AdminSignupRequests from "@/pages/admin/AdminSignupRequests";
import BillingReturn from "@/pages/pro/BillingReturn";
import AdminPlatform from "@/pages/admin/AdminPlatform";
import AccountSecurity from "@/pages/AccountSecurity";
import Inventory from "@/modules/inventory/Inventory";
import { PasswordGate } from "@/components/PasswordGate";
import SupportChat, { AdminSupportInbox } from "@/modules/support/SupportChat";
import Board from "@/modules/interventions/Board";
import VehicleDetail from "@/pages/pro/VehicleDetail";
import Finance from "@/modules/dashboards/Home";
import MyDay from "@/modules/interventions/MyDay";
import SecureAction from "@/pages/public/SecureAction";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PasswordGate />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/account/security" element={<AccountSecurity />} />
            <Route path="/signup/status" element={<SignupStatus />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/s" element={<SecureAction />} />
            <Route path="/account" element={<RequireAuth><AccountHub /></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth admin><AdminShell /></RequireAuth>}>
              <Route index element={<AdminDashboard />} />
              <Route path="garages" element={<AdminGarages />} />
              <Route path="garages/:id" element={<AdminGarageDetail />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="platform" element={<AdminPlatform />} />
              <Route path="signup-requests" element={<AdminSignupRequests />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="support-inbox" element={<AdminSupportInbox />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="outbox" element={<AdminOutbox />} />
            </Route>
            <Route path="/pro" element={<RequireAuth><GarageProvider><ProShell /></GarageProvider></RequireAuth>}>
              <Route index element={<ProDashboard />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="vehicles/:id" element={<VehicleDetail />} />
              <Route path="finance" element={<Finance />} />
              <Route path="planning" element={<Planning />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="board" element={<Board />} />
              <Route path="my-day" element={<MyDay />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="quotes" element={<Quotes />} />
              <Route path="quotes/:id" element={<QuoteEditor />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />
              <Route path="messages" element={<Inbox />} />
              <Route path="followups" element={<FollowUps />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="availability" element={<Availability />} />
              <Route path="employees" element={<Employees />} />
              <Route path="settings" element={<Settings />} />
              <Route path="billing/return" element={<BillingReturn />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="support" element={<SupportChat />} />
            </Route>
            <Route path="/g/:slug" element={<PortalLayout />}>
              <Route index element={<PortalLanding />} />
              <Route path="login" element={<PortalLogin />} />
              <Route path="signup" element={<PortalSignup />} />
              <Route path="forgot-password" element={<PortalForgot />} />
              <Route path="account" element={<PortalAccount />} />
              <Route path="book" element={<PortalBook />} />
              <Route path="jobs/:id" element={<PortalJob />} />
              <Route path="quotes/:id" element={<PortalQuote />} />
              <Route path="invoices/:id" element={<PortalInvoice />} />
              <Route path="*" element={<NotFound inPortal />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
